/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Loading backdrop, success toasts, and confirmation modals for ViewAppointments.
 * @module pages/viewAppointments/ViewAppointmentsModalsAndToasts
 */

import ConfirmationModal from "../../modals/ConfirmationModal/index.jsx";
import VirtualMeetingModal from "../../modals/VirtualMeetingModal/index.jsx";
import { Backdrop, CircularProgress } from "@mui/material";
import styles from "../../styles/ViewAppointments.module.css";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/BusinessEvents/CheckCircleIcon.svg";

const ViewAppointmentsModalsAndToasts = ({
  t,
  isLoading,
  showSuccessMessage,
  isSlidingUp,
  showRescheduleSuccessMessage,
  isRescheduleSlidingUp,
  showConfirmModal,
  showVirtualMeetingModal,
  showVirtualAppointmentErrorModal,
  selectedAppointmentForVirtual,
  isConvertingToVirtual,
  virtualAppointmentError,
  eventData,
  confirmationDialogRef,
  virtualAppointmentDialogRef,
  virtualAppointmentErrorDialogRef,
  onConfirmModalClose,
  onConfirmModalConfirm,
  onVirtualAppointmentClose,
  onVirtualAppointmentConfirm,
  onVirtualAppointmentErrorClose,
}) => (
  <>
    <Backdrop
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        backdropFilter: "blur(4px)",
      }}
      open={isLoading}
    >
      <CircularProgress size={60} thickness={4} sx={{ color: "#4eabe7" }} />
    </Backdrop>

    {showSuccessMessage && (
      <div
        className={`${styles.successMessage} ${
          isSlidingUp ? styles.slideUp : ""
        }`}
      >
        <CheckCircleIcon className={styles.successMessageIcon} />
        {t("pages.viewAppointments.scheduleSuccessMessage")}
      </div>
    )}

    {showRescheduleSuccessMessage && (
      <div
        className={`${styles.successMessage} ${
          isRescheduleSlidingUp ? styles.slideUp : ""
        }`}
      >
        <CheckCircleIcon className={styles.successMessageIcon} />
        {t("pages.viewAppointments.rescheduleSuccessMessage")}
      </div>
    )}

    {showConfirmModal && (
      <ConfirmationModal
        dialogRef={confirmationDialogRef}
        open={showConfirmModal}
        onClose={onConfirmModalClose}
        onConfirm={onConfirmModalConfirm}
        title={t("pages.viewAppointments.confirmModalTitle")}
        message={t("pages.viewAppointments.confirmModalMessage")}
        confirmButtonText={t("common.confirm")}
        cancelButtonText={t("common.cancel")}
        isLoading={isLoading}
        loadingButtonText={t("common.saving")}
      />
    )}

    {showVirtualMeetingModal && selectedAppointmentForVirtual && (
      <VirtualMeetingModal
        dialogRef={virtualAppointmentDialogRef}
        open={showVirtualMeetingModal}
        onClose={onVirtualAppointmentClose}
        onConfirm={onVirtualAppointmentConfirm}
        isLoading={isConvertingToVirtual}
        selectedAppointment={selectedAppointmentForVirtual}
        eventData={eventData}
      />
    )}

    {showVirtualAppointmentErrorModal && (
      <ConfirmationModal
        dialogRef={virtualAppointmentErrorDialogRef}
        open={showVirtualAppointmentErrorModal}
        onClose={onVirtualAppointmentErrorClose}
        onConfirm={onVirtualAppointmentErrorClose}
        title={t("pages.viewAppointments.virtualAppointmentErrorTitle")}
        message={virtualAppointmentError}
        confirmButtonText={t("common.close")}
        cancelButtonText=""
        isLoading={false}
        loadingButtonText=""
      />
    )}
  </>
);

export default ViewAppointmentsModalsAndToasts;
